import { FC, ReactNode } from 'react'

import * as ToastPrimitive from '@radix-ui/react-toast'
import { styled, keyframes } from 'styled-components'

import Icon from 'core/components/Icon'
import { variables } from 'core/styles'

export type ToastData = {
  id: string
  message: ReactNode
  type: 'error' | 'warning' | 'success'
}

type ToastProps = ToastData & {
  onOpenChange?: (value: boolean) => void
}

const Toast: FC<ToastProps> = ({ message, type, onOpenChange }) => (
  <ToastPrimitive.Root asChild onOpenChange={onOpenChange}>
    <StyledToast $type={type}>
      <StyledDescription>{message}</StyledDescription>
      <ToastPrimitive.Close asChild>
        <StyledDismissButton aria-label='Dismiss'>
          <Icon fontSize='14px' name='clear' />
        </StyledDismissButton>
      </ToastPrimitive.Close>
    </StyledToast>
  </ToastPrimitive.Root>
)

export default Toast

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const slideIn = keyframes`
  from {
    transform: translateX(calc(100% + 16px));
  }
  to {
    transform: translateX(0);
  }
`

const StyledToast = styled.li<{ $type: ToastProps['type'] }>`
  --toastBg: ${(p) =>
    p.$type === 'error'
      ? variables.colorRedLighten
      : p.$type === 'warning'
      ? variables.colorOrangeLighten
      : variables.colorGreenLighten};

  display: flex;
  position: relative;
  align-items: center;

  /* Fallback for browsers without color-mix() support */
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  box-shadow: ${variables.peachyShadowSmall};
  background-color: var(--toastBg);
  padding: 16px;
  max-width: 480px;
  color: ${variables.colorBlack100};

  & + & {
    margin-top: 12px;
  }

  /* open and close animation */
  &[data-state='open'] {
    animation: ${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-state='closed'] {
    animation: ${fadeOut} 100ms ease-in;
  }

  /* swipe gesture animation */
  &[data-swipe='cancel'] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  /* Prefer a border derived from the toast background (slightly darker) */
  @supports (color: color-mix(in oklab, white, black)) {
    border-color: color-mix(in oklab, var(--toastBg) 88%, black 12%);
  }
`

const StyledDescription = styled(ToastPrimitive.Description)`
  flex-grow: 1;
`

const StyledDismissButton = styled.button`
  display: flex;
  position: absolute;
  top: -8px;
  right: -8px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 50%;
  background-color: ${(p) => p.theme.background};
  cursor: pointer;
  width: 24px;
  height: 24px;
  color: ${(p) => p.theme.text};

  &:hover {
    background-color: ${(p) => p.theme.hover};
  }
`
